/**
 * Account Page - Manage account settings, subscription, and extension linking
 */

import { API_ENDPOINTS } from '@refinevoice/shared';
import { useCallback, useEffect, useState } from 'react';
import { ModalShell } from '../components/ModalShell';
import { SignInModal } from '../components/SignInModal';
import { useAuth } from '../contexts/AuthContext';
import './Account.css';

interface ExtensionLinkResponse {
  token: string;
  email: string;
  message: string;
}

interface UsageStats {
  recordingSeconds: { last30Days: number };
  refines: { last30Days: number };
}

interface SubscriptionLimits {
  minutes: number;
  refines: number;
}

// Tier limits
const TIER_LIMITS: Record<string, SubscriptionLimits> = {
  free: { minutes: 30, refines: 30 },
  starter: { minutes: 120, refines: 100 },
  pro: { minutes: 480, refines: 500 },
  business: { minutes: 1200, refines: 1000 },
  enterprise: { minutes: 5000, refines: -1 },
};

// Pricing tiers configuration
const PRICING_TIERS = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    period: '',
    features: ['30 min/month', '3 min/session', '30 refines'],
  },
  {
    id: 'starter',
    name: 'Starter',
    price: 4,
    period: '/mo',
    features: ['120 min/month', '10 min/session', '100 refines'],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 9,
    period: '/mo',
    popular: true,
    features: ['480 min/month', '60 min/session', '500 refines', 'Overage €0.02/min'],
  },
  {
    id: 'business',
    name: 'Business',
    price: 29,
    period: '/mo',
    features: ['1,200 min/month', '120 min/session', '1,000 refines', 'Widget (1 domain)'],
    waitlist: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 99,
    period: '/mo',
    features: ['5,000 min/month', 'Unlimited sessions', 'Unlimited refines', 'Widget (unlimited)'],
    waitlist: true,
  },
];

/**
 * Validate that a redirect target is a Stripe-owned HTTPS URL before
 * navigating to it. Guards against an open redirect if the API response
 * is ever spoofed or tampered with.
 */
function assertStripeUrl(url: string): string {
  const parsed = new URL(url);
  const host = parsed.hostname;
  if (parsed.protocol !== 'https:' || !(host === 'stripe.com' || host.endsWith('.stripe.com'))) {
    throw new Error('Unexpected redirect target');
  }
  return parsed.toString();
}

export function Account() {
  const { isAuthenticated, email, signOut } = useAuth();
  const [showSignIn, setShowSignIn] = useState(false);
  const [isLinkingExtension, setIsLinkingExtension] = useState(false);
  const [extensionLinked, setExtensionLinked] = useState(false);
  const [extensionError, setExtensionError] = useState<string | null>(null);
  const [currentTier, setCurrentTier] = useState('free');
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [usage, setUsage] = useState<UsageStats | null>(null);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [waitlistTier, setWaitlistTier] = useState<string | null>(null);
  const [waitlistForm, setWaitlistForm] = useState({ website: '', technology: '' });
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const [waitlistError, setWaitlistError] = useState<string | null>(null);

  // Check if opened from extension with ?extension=link or checkout result
  const urlParams = new URLSearchParams(window.location.search);
  const isExtensionLinking = urlParams.get('extension') === 'link';
  const checkoutResult = urlParams.get('checkout');

  // Fetch current subscription and usage
  useEffect(() => {
    if (!isAuthenticated) {
      setIsLoadingSubscription(false);
      return;
    }

    const fetchData = async () => {
      try {
        const [subResponse, usageResponse] = await Promise.all([
          fetch('/api/stripe/subscription', { credentials: 'include' }),
          fetch('/api/usage', { credentials: 'include' }),
        ]);

        if (subResponse.ok) {
          const data = await subResponse.json();
          setCurrentTier(data.tier || 'free');
        }

        if (usageResponse.ok) {
          const data = await usageResponse.json();
          setUsage(data);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setIsLoadingSubscription(false);
      }
    };

    fetchData();
  }, [isAuthenticated]);

  // Handle checkout result from URL
  useEffect(() => {
    if (checkoutResult === 'success') {
      const refreshSubscription = async () => {
        try {
          const response = await fetch('/api/stripe/subscription', { credentials: 'include' });
          if (response.ok) {
            const data = await response.json();
            setCurrentTier(data.tier || 'free');
          }
        } catch (error) {
          console.error('Failed to refresh subscription:', error);
        }
      };
      refreshSubscription();
      window.history.replaceState({}, '', '/account');
    } else if (checkoutResult === 'cancelled') {
      window.history.replaceState({}, '', '/account');
    }
  }, [checkoutResult]);

  const handleLinkExtension = useCallback(async () => {
    if (!isAuthenticated) {
      setShowSignIn(true);
      return;
    }

    setIsLinkingExtension(true);
    setExtensionError(null);

    try {
      const response = await fetch(API_ENDPOINTS.EXTENSION_LINK, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to link extension');
      }

      const data: ExtensionLinkResponse = await response.json();

      sessionStorage.setItem('refinevoice_extension_token', data.token);
      sessionStorage.setItem('refinevoice_extension_email', data.email);

      window.dispatchEvent(
        new CustomEvent('refinevoice-extension-linked', {
          detail: { token: data.token, email: data.email },
        })
      );

      setExtensionLinked(true);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setExtensionError(errorMessage);
    } finally {
      setIsLinkingExtension(false);
    }
  }, [isAuthenticated]);

  // Auto-link extension when authenticated and coming from extension
  useEffect(() => {
    if (isAuthenticated && isExtensionLinking && !extensionLinked && !isLinkingExtension) {
      handleLinkExtension();
    }
  }, [
    isAuthenticated,
    isExtensionLinking,
    extensionLinked,
    isLinkingExtension,
    handleLinkExtension,
  ]);

  // Handle tier selection
  const handleTierSelect = async (tier: (typeof PRICING_TIERS)[0]) => {
    if (tier.id === currentTier) return;

    if (tier.waitlist) {
      setShowPricingModal(false);
      setWaitlistTier(tier.id);
      setShowWaitlistModal(true);
      setWaitlistSubmitted(false);
      setWaitlistError(null);
      setWaitlistForm({ website: '', technology: '' });
    } else if (tier.id !== 'free') {
      setIsCheckingOut(true);
      setCheckoutError(null);

      try {
        const response = await fetch('/api/stripe/checkout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ tier: tier.id, billing: 'monthly' }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || error.message || 'Failed to create checkout');
        }

        const { url } = await response.json();
        if (url) {
          window.location.href = assertStripeUrl(url);
        }
      } catch (error) {
        setCheckoutError(error instanceof Error ? error.message : 'Checkout failed');
        setIsCheckingOut(false);
      }
    }
  };

  // Handle manage subscription (billing portal)
  const handleManageSubscription = async () => {
    try {
      const response = await fetch('/api/stripe/portal', {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to open billing portal');
      }

      const { url } = await response.json();
      if (url) {
        window.location.href = assertStripeUrl(url);
      }
    } catch (error) {
      console.error('Portal error:', error);
    }
  };

  // Handle waitlist form submission
  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setWaitlistError(null);

    try {
      const response = await fetch('/api/extension/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          tier: waitlistTier,
          website: waitlistForm.website,
          technology: waitlistForm.technology,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to join waitlist');
      }

      setWaitlistSubmitted(true);
    } catch (error) {
      setWaitlistError(error instanceof Error ? error.message : 'Unknown error');
    }
  };

  // Calculate usage percentages
  const limits = TIER_LIMITS[currentTier] || TIER_LIMITS.free;
  const minutesUsed = usage ? Math.round(usage.recordingSeconds.last30Days / 60) : 0;
  const refinesUsed = usage?.refines.last30Days || 0;
  const minutesPercent =
    limits.minutes > 0 ? Math.min(100, (minutesUsed / limits.minutes) * 100) : 0;
  const refinesPercent =
    limits.refines > 0 ? Math.min(100, (refinesUsed / limits.refines) * 100) : 0;

  // Get tier display name
  const tierDisplayName = PRICING_TIERS.find((t) => t.id === currentTier)?.name || 'Free';

  // Show dedicated success view when extension linking completed
  if (isExtensionLinking && extensionLinked && isAuthenticated) {
    return (
      <div className="account-page">
        <div className="account-card extension-link-success">
          <div className="success-icon-large">
            <svg viewBox="0 0 24 24" width="64" height="64" fill="currentColor" aria-hidden="true">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
            </svg>
          </div>
          <h1>Extension Linked!</h1>
          <p className="account-description">
            Your account <strong>{email}</strong> is now connected to the Chrome extension.
          </p>
          <p className="account-description">
            You can close this tab and start using voice-to-text anywhere.
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="account-page">
        <div className="account-card">
          <h1>Account</h1>
          <p className="account-description">
            {isExtensionLinking
              ? 'Sign in to link your RefineVoice Chrome extension.'
              : 'Sign in to manage your RefineVoice account.'}
          </p>
          <button
            type="button"
            className="account-btn account-btn-primary"
            onClick={() => setShowSignIn(true)}
          >
            Sign In
          </button>
        </div>
        {showSignIn && <SignInModal isOpen={showSignIn} onClose={() => setShowSignIn(false)} />}
      </div>
    );
  }

  return (
    <div className="account-page">
      <div className="account-card account-card-wide">
        <h1>Account</h1>

        {/* Profile Section */}
        <div className="account-section">
          <h2>Profile</h2>
          <div className="account-info">
            <span className="account-label">Email</span>
            <span className="account-value">{email}</span>
          </div>
        </div>

        {/* Plan & Usage Section */}
        <div className="account-section">
          <div className="section-header">
            <h2>Plan & Usage</h2>
            {currentTier !== 'free' && (
              <button type="button" className="account-btn-link" onClick={handleManageSubscription}>
                Manage billing
              </button>
            )}
          </div>

          <div className="plan-info">
            <div className="plan-badge">
              <span className="plan-name">{tierDisplayName}</span>
              {currentTier === 'free' && (
                <button
                  type="button"
                  className="account-btn-link"
                  onClick={() => setShowPricingModal(true)}
                >
                  Upgrade
                </button>
              )}
            </div>
          </div>

          {!isLoadingSubscription && (
            <div className="usage-stats">
              <div className="usage-item">
                <div className="usage-header">
                  <span className="usage-label">Recording</span>
                  <span className="usage-value">
                    {minutesUsed} / {limits.minutes} min
                  </span>
                </div>
                <div className="usage-bar">
                  <div
                    className={`usage-bar-fill ${minutesPercent >= 90 ? 'warning' : ''}`}
                    style={{ width: `${minutesPercent}%` }}
                  />
                </div>
              </div>

              <div className="usage-item">
                <div className="usage-header">
                  <span className="usage-label">Refines</span>
                  <span className="usage-value">
                    {refinesUsed} / {limits.refines === -1 ? '∞' : limits.refines}
                  </span>
                </div>
                <div className="usage-bar">
                  <div
                    className={`usage-bar-fill ${refinesPercent >= 90 ? 'warning' : ''}`}
                    style={{ width: `${refinesPercent}%` }}
                  />
                </div>
              </div>

              <p className="usage-note">Usage resets monthly</p>
            </div>
          )}

          {currentTier === 'free' && (
            <button
              type="button"
              className="account-btn account-btn-primary"
              onClick={() => setShowPricingModal(true)}
            >
              Upgrade Plan
            </button>
          )}
        </div>

        {/* Chrome Extension Section */}
        <div className="account-section">
          <h2>Chrome Extension</h2>
          {extensionLinked ? (
            <div className="extension-success">
              <div className="success-icon">
                <svg
                  viewBox="0 0 24 24"
                  width="32"
                  height="32"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                </svg>
              </div>
              <div>
                <h3>Extension Linked!</h3>
                <p>You can close this tab and start using voice-to-text.</p>
              </div>
            </div>
          ) : (
            <>
              <p className="account-description">
                Link your Chrome extension to enable voice-to-text on any website.
              </p>
              {extensionError && <div className="account-error">{extensionError}</div>}
              <button
                type="button"
                className="account-btn account-btn-primary"
                onClick={handleLinkExtension}
                disabled={isLinkingExtension}
              >
                {isLinkingExtension ? 'Linking...' : 'Link Extension'}
              </button>
            </>
          )}
        </div>

        {/* Sign Out */}
        <div className="account-section">
          <button type="button" className="account-btn account-btn-secondary" onClick={signOut}>
            Sign Out
          </button>
        </div>
      </div>

      {/* Pricing Modal */}
      {showPricingModal && (
        <ModalShell
          onClose={() => setShowPricingModal(false)}
          contentClassName="modal-pricing"
          labelledBy="pricing-modal-title"
        >
          <button
            type="button"
            className="modal-close"
            onClick={() => setShowPricingModal(false)}
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
            </svg>
          </button>

          <h2 id="pricing-modal-title">Choose Your Plan</h2>
          <p className="modal-subtitle">All plans include the Chrome extension</p>

          {checkoutError && <div className="account-error">{checkoutError}</div>}

          <div className="pricing-grid-modal">
            {PRICING_TIERS.filter((t) => t.id !== 'free').map((tier) => (
              <div
                key={tier.id}
                className={`pricing-card-modal ${tier.id === currentTier ? 'current' : ''} ${tier.popular ? 'popular' : ''}`}
              >
                {tier.popular && <div className="popular-badge">Popular</div>}
                <h3>{tier.name}</h3>
                <div className="pricing-price">
                  <span className="price-currency">€</span>
                  <span className="price-amount">{tier.price}</span>
                  <span className="price-period">{tier.period}</span>
                </div>
                <ul className="pricing-features-compact">
                  {tier.features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>
                <button
                  type="button"
                  className={`pricing-btn ${tier.popular ? 'popular' : ''} ${tier.id === currentTier ? 'current' : ''}`}
                  onClick={() => handleTierSelect(tier)}
                  disabled={tier.id === currentTier || isCheckingOut}
                >
                  {tier.id === currentTier
                    ? 'Current'
                    : tier.waitlist
                      ? 'Join Waitlist'
                      : isCheckingOut
                        ? 'Loading...'
                        : 'Select'}
                </button>
              </div>
            ))}
          </div>
        </ModalShell>
      )}

      {/* Waitlist Modal */}
      {showWaitlistModal && (
        <ModalShell onClose={() => setShowWaitlistModal(false)} ariaLabel="Join the waitlist">
          <button
            type="button"
            className="modal-close"
            onClick={() => setShowWaitlistModal(false)}
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor" aria-hidden="true">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
            </svg>
          </button>

          {waitlistSubmitted ? (
            <div className="waitlist-success">
              <div className="success-icon">
                <svg
                  viewBox="0 0 24 24"
                  width="48"
                  height="48"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                </svg>
              </div>
              <h3>You're on the list!</h3>
              <p>We'll notify you when this plan is available.</p>
              <button
                type="button"
                className="account-btn account-btn-primary"
                onClick={() => setShowWaitlistModal(false)}
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <h3>Join the Waitlist</h3>
              <p>Tell us about your project and we'll notify you when it's ready.</p>

              <form onSubmit={handleWaitlistSubmit}>
                <div className="form-group">
                  <label htmlFor="website">Website URL</label>
                  <input
                    type="url"
                    id="website"
                    placeholder="https://example.com"
                    value={waitlistForm.website}
                    onChange={(e) => setWaitlistForm({ ...waitlistForm, website: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="technology">Technology / Framework</label>
                  <select
                    id="technology"
                    value={waitlistForm.technology}
                    onChange={(e) =>
                      setWaitlistForm({ ...waitlistForm, technology: e.target.value })
                    }
                    required
                  >
                    <option value="">Select...</option>
                    <option value="react">React</option>
                    <option value="vue">Vue</option>
                    <option value="angular">Angular</option>
                    <option value="svelte">Svelte</option>
                    <option value="nextjs">Next.js</option>
                    <option value="nuxt">Nuxt</option>
                    <option value="wordpress">WordPress</option>
                    <option value="shopify">Shopify</option>
                    <option value="vanilla">Vanilla JS / HTML</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {waitlistError && <div className="account-error">{waitlistError}</div>}

                <button type="submit" className="account-btn account-btn-primary">
                  Join Waitlist
                </button>
              </form>
            </>
          )}
        </ModalShell>
      )}
    </div>
  );
}
